import { useMemo, useState } from "react";
import { Loader2, Plus, Edit3, Power, Save, X, Search } from "lucide-react";
import {
  useCuentas,
  useCreateCuenta,
  useUpdateCuenta,
  useDeleteCuenta,
} from "../api";
import { useAccountingStore } from "../store";
import { AccountingHeader } from "../components/AccountingHeader";
import type { Cuenta, CuentaFormData, Naturaleza } from "../types";
import { Pagination, pageSlice } from "@modules/billing/components/Pagination";

const GRUPO_LABEL: Record<number, string> = {
  1: "1 · Financiación básica",
  2: "2 · Inmovilizado",
  3: "3 · Existencias",
  4: "4 · Acreedores/Deudores",
  5: "5 · Cuentas financieras",
  6: "6 · Compras y gastos",
  7: "7 · Ventas e ingresos",
};

const EMPTY: Omit<CuentaFormData, "emisor_id"> = {
  codigo: "",
  nombre: "",
  grupo: 1,
  naturaleza: "d",
  activa: true,
};

export function PlanCuentasPage() {
  const { emisorId } = useAccountingStore();
  const [grupo, setGrupo] = useState<number | undefined>();
  const [q, setQ] = useState("");
  const { data: cuentas, isLoading } = useCuentas(emisorId, { grupo, q: q || undefined });

  const createMut = useCreateCuenta();
  const updateMut = useUpdateCuenta();
  const deleteMut = useDeleteCuenta();

  const [editing, setEditing] = useState<Cuenta | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<Omit<CuentaFormData, "emisor_id">>(EMPTY);
  const [formError, setFormError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const paged = useMemo(() => pageSlice(cuentas, page, pageSize), [cuentas, page, pageSize]);

  function openCreate() {
    setForm(EMPTY);
    setCreating(true);
    setEditing(null);
    setFormError(null);
  }
  function openEdit(c: Cuenta) {
    setForm({
      codigo: c.codigo,
      nombre: c.nombre,
      grupo: c.grupo,
      naturaleza: c.naturaleza,
      activa: c.activa,
    });
    setEditing(c);
    setCreating(false);
    setFormError(null);
  }
  function close() {
    setEditing(null);
    setCreating(false);
    setFormError(null);
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!emisorId) return;
    setFormError(null);
    try {
      if (editing) {
        await updateMut.mutateAsync({
          id: editing.id,
          payload: {
            nombre: form.nombre,
            grupo: form.grupo,
            naturaleza: form.naturaleza,
            activa: form.activa,
          },
        });
      } else {
        await createMut.mutateAsync({ ...form, emisor_id: emisorId });
      }
      close();
    } catch (err: unknown) {
      const e2 = err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } };
      const first = e2?.response?.data?.errors
        ? Object.values(e2.response.data.errors).flat()[0]
        : null;
      setFormError(first ?? e2?.response?.data?.message ?? "Error guardando.");
    }
  }

  return (
    <div className="p-8 h-full flex flex-col gap-5 min-h-0">
      <AccountingHeader
        title="Contabilidad"
        subtitle="Plan general contable del emisor seleccionado."
        showEjercicio={false}
        right={
          <button
            onClick={openCreate}
            disabled={!emisorId}
            className="inline-flex items-center gap-2 rounded-xl bg-brand-700 hover:bg-brand-800 disabled:opacity-50 text-white px-4 py-2 font-bold text-sm shadow-soft transition self-end"
          >
            <Plus size={16} /> Nueva cuenta
          </button>
        }
      />

      <div className="flex flex-wrap gap-3 shrink-0">
        <select
          value={grupo ?? ""}
          onChange={(e) => {
            setGrupo(e.target.value ? Number(e.target.value) : undefined);
            setPage(1);
          }}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="">Todos los grupos</option>
          {Object.entries(GRUPO_LABEL).map(([g, label]) => (
            <option key={g} value={g}>
              {label}
            </option>
          ))}
        </select>
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder="Buscar por código o nombre…"
            className="w-full rounded-lg border border-slate-300 pl-9 pr-3 py-2 text-sm"
          />
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Cargando…
        </div>
      )}

      {cuentas && (
        <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 overflow-hidden flex flex-col">
          <div className="overflow-auto max-h-[calc(100vh-340px)]">
            <table className="w-full">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-4 py-3 font-bold w-32">Código</th>
                  <th className="text-left px-4 py-3 font-bold">Nombre</th>
                  <th className="text-center px-4 py-3 font-bold w-20">Grupo</th>
                  <th className="text-center px-4 py-3 font-bold w-24">Naturaleza</th>
                  <th className="text-center px-4 py-3 font-bold w-24">Estado</th>
                  <th className="text-center px-4 py-3 font-bold w-20">Activa</th>
                  <th className="text-right px-4 py-3 font-bold w-24">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {cuentas.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                      No hay cuentas.
                    </td>
                  </tr>
                )}
                {paged.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                    <td className="px-4 py-2.5 font-mono font-bold text-slate-900">{c.codigo}</td>
                    <td className="px-4 py-2.5 text-sm">{c.nombre}</td>
                    <td className="px-4 py-2.5 text-center font-mono text-sm">{c.grupo}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold border border-slate-200 bg-slate-50 text-slate-700">
                        {c.naturaleza === "d" ? "Deudora" : "Acreedora"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold border ${
                          c.estado === "balance"
                            ? "bg-sky-50 text-sky-700 border-sky-200"
                            : "bg-violet-50 text-violet-700 border-violet-200"
                        }`}
                      >
                        {c.estado === "balance" ? "Balance" : "PyG"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      {c.activa ? (
                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />
                      ) : (
                        <span className="w-2.5 h-2.5 rounded-full bg-slate-300 inline-block" />
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(c)}
                          className="p-1.5 rounded hover:bg-slate-100 text-slate-700"
                          title="Editar"
                        >
                          <Edit3 size={15} />
                        </button>
                        {c.activa && (
                          <button
                            onClick={() => {
                              if (confirm(`Desactivar la cuenta ${c.codigo}?`)) deleteMut.mutate(c.id);
                            }}
                            className="p-1.5 rounded hover:bg-rose-50 text-rose-700"
                            title="Desactivar"
                          >
                            <Power size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={cuentas.length}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(1);
            }}
          />
        </div>
      )}

      {(creating || editing) && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
            <header className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-black text-slate-900">
                {editing ? `Editar cuenta ${editing.codigo}` : "Nueva cuenta"}
              </h2>
              <button type="button" onClick={close} className="text-slate-400 hover:text-slate-700">
                <X size={20} />
              </button>
            </header>
            {formError && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 p-3 text-sm">
                {formError}
              </div>
            )}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Código *
                  </label>
                  <input
                    required
                    type="text"
                    disabled={!!editing}
                    value={form.codigo}
                    onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                    placeholder="ej: 430000001"
                    className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono disabled:bg-slate-50"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Grupo *
                  </label>
                  <select
                    value={form.grupo}
                    onChange={(e) => setForm({ ...form, grupo: Number(e.target.value) })}
                    className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    {Object.entries(GRUPO_LABEL).map(([g, label]) => (
                      <option key={g} value={g}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Nombre *
                </label>
                <input
                  required
                  type="text"
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Naturaleza *
                  </label>
                  <select
                    value={form.naturaleza}
                    onChange={(e) =>
                      setForm({ ...form, naturaleza: e.target.value as Naturaleza })
                    }
                    className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="d">Deudora</option>
                    <option value="a">Acreedora</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm pb-2">
                    <input
                      type="checkbox"
                      checked={form.activa ?? true}
                      onChange={(e) => setForm({ ...form, activa: e.target.checked })}
                      className="rounded border-slate-300 text-brand-700"
                    />
                    <span className="font-bold text-slate-700">Cuenta activa</span>
                  </label>
                </div>
              </div>
              <p className="text-xs text-slate-400">
                El estado (Balance/PyG) se asigna automáticamente según el grupo (1-5 → Balance, 6-7
                → PyG).
              </p>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={close}
                className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-bold hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={createMut.isPending || updateMut.isPending}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
              >
                <Save size={14} /> Guardar
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
